import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import api from '../services/api';
import type {
  User,
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  SocialLoginRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  VerifyEmailRequest,
} from '../types';
import { UserRole } from '../types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isModerator: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// User data only - token is in HttpOnly cookie (not accessible via JS)
const USER_KEY = 'cartcritic_user';

export function AuthProvider({ children }: { children: ReactNode }) {
  // Initialize user from localStorage synchronously to prevent flash of unauthenticated UI
  const [user, setUser] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem(USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(true);

  // Verify the cookie is still valid with the server
  useEffect(() => {
    const verifyAuth = async () => {
      const storedUser = localStorage.getItem(USER_KEY);

      if (storedUser) {
        try {
          const response = await api.get<User>('/auth/me');
          setUser(response.data);
          localStorage.setItem(USER_KEY, JSON.stringify(response.data));
        } catch {
          // Cookie expired or invalid - clear local state
          localStorage.removeItem(USER_KEY);
          setUser(null);
        }
      }

      setIsLoading(false);
    };

    verifyAuth();
  }, []);

  // Save user data to localStorage (token is in HttpOnly cookie)
  const saveAuthState = useCallback((response: AuthResponse) => {
    localStorage.setItem(USER_KEY, JSON.stringify(response.user));
    setUser(response.user);
  }, []);

  const login = useCallback(async (credentials: LoginRequest) => {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    saveAuthState(response.data);
  }, [saveAuthState]);

  const loginWithGoogle = useCallback(async (idToken: string) => {
    const request: SocialLoginRequest = {
      provider: 'google',
      idToken,
    };
    const response = await api.post<AuthResponse>('/auth/social', request);
    saveAuthState(response.data);
  }, [saveAuthState]);

  const register = useCallback(async (data: RegisterRequest) => {
    const response = await api.post<AuthResponse>('/auth/register', data);
    saveAuthState(response.data);
  }, [saveAuthState]);

  const logout = useCallback(async () => {
    try {
      // Call backend to clear HttpOnly cookie
      await api.post('/auth/logout');
    } catch {
      // Ignore errors - proceed with local cleanup
    }
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    const request: ForgotPasswordRequest = { email };
    await api.post('/auth/forgot-password', request);
  }, []);

  const resetPassword = useCallback(async (token: string, newPassword: string) => {
    const request: ResetPasswordRequest = { token, newPassword };
    await api.post('/auth/reset-password', request);
  }, []);

  const verifyEmail = useCallback(async (token: string) => {
    const request: VerifyEmailRequest = { token };
    await api.post('/auth/verify-email', request);

    // Update user state to reflect verified email
    if (user) {
      const updatedUser = { ...user, isEmailVerified: true };
      localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
      setUser(updatedUser);
    }
  }, [user]);

  const resendVerificationEmail = useCallback(async () => {
    await api.post('/auth/resend-verification');
  }, []);

  const deleteAccount = useCallback(async () => {
    await api.delete('/users/me');
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  const updateUser = useCallback((updatedUser: User) => {
    localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
    setUser(updatedUser);
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === UserRole.Admin,
    isModerator: user?.role === UserRole.Moderator || user?.role === UserRole.Admin,
    login,
    loginWithGoogle,
    register,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerificationEmail,
    deleteAccount,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
